import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../hooks/useGTM";
import { trackEvent as trackClarityEvent } from "../../hooks/useClarity";
import { metaPixelCatalogService } from "../../services/metaPixelCatalogService";

import styles from "./BlogBanner.module.css";

interface BannerProps {
  banners: Array<{
    id: number;
    alt: string;
    display: "desktop" | "mobile";
    image: string;
    title: string;
  }>;
}

const BlogBanner: React.FC<BannerProps> = ({ banners }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [searchQuery, setSearchQuery] = useState("");
  const imageBaseUrl = process.env.REACT_APP_IMAGE_FOLDER;
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const currentBanner = banners.find((banner) =>
    isMobile ? banner.display === "mobile" : banner.display === "desktop"
  );

  if (!currentBanner) return null;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Track blog search event in GTM
      trackEvent("blog_search", {
        search_string: searchQuery.trim(), // Changed from search_term to search_string for consistency
        search_type: "blog_banner",
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Track blog search event in Clarity
      trackClarityEvent("blog_search", {
        search_string: searchQuery.trim(), // Changed from search_term to search_string for consistency
        search_type: "blog_banner",
        device_type: isMobile ? "mobile" : "desktop",
      });

      // Track Meta Pixel blog search
      metaPixelCatalogService.trackBlogSearch(
        searchQuery.trim(),
        0, // Results count will be updated on search results page
        "blog_banner"
      );

      navigate(`/blog/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className={styles.banner}>
      <img
        src={`${currentBanner.image}`}
        alt={currentBanner.alt}
        className={styles.bannerImage}
      />
      <div className={styles.overlay}>
        <div className={styles.content}>
          <h1 className={styles.title}>{currentBanner.title}</h1>
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <div className={styles.searchWrapper}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar artículos..."
                className={styles.searchInput}
              />
              <button
                type="submit"
                className={styles.searchButton}
                aria-label="Buscar en el blog"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className={styles.searchIcon}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogBanner;
